import React, { useState } from "react";
import { Copy, Check, Gift, CheckCircle2, Clock } from "lucide-react";
import { usd, itemKey, personKey } from "../../lib/api";
import { EmailGate } from "../EmailGate";

const PILL = {
  confirmed: ["bg-[#008057] text-white", "Confirmed"],
  pending_payment: ["bg-amber-100 text-amber-800", "Pending Payment"],
  cancelled: ["bg-red-100 text-red-700", "Cancelled"],
};
const JERSEY_SIZES = ["6-7Y", "8-9Y", "10-11Y", "12-13Y", "14-15Y", "Adult S", "Adult M", "Adult L", "Adult XL"];

const StatusPill = ({ status }) => {
  const [cls, label] = PILL[status] || ["bg-slate-100 text-slate-600", status];
  return <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${cls}`}>{label}</span>;
};

export function ReceiptHeader({ order: o }) {
  const [copied, setCopied] = useState(false);
  const confirmed = o.registration_status === "confirmed";
  const copy = () => { navigator.clipboard.writeText(o.reference); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className={`px-6 py-6 ${confirmed ? "bg-[#008057]" : "bg-[#03281B]"} text-white`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#F3C242] font-bold">Registration Reference</div>
          <div className="font-display text-3xl font-extrabold flex items-center gap-3">{o.reference}
            <button data-testid="copy-reference" onClick={copy} className="text-white/70 hover:text-white">{copied ? <Check size={18} /> : <Copy size={18} />}</button>
          </div>
        </div>
        {confirmed ? <CheckCircle2 size={40} /> : <Clock size={40} />}
      </div>
      <div className="mt-3"><StatusPill status={o.registration_status} /></div>
    </div>
  );
}

export const BankTransferNotice = ({ order: o, bank }) => (
  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="bank-transfer-notice">
    <div className="font-bold text-amber-800 mb-2">Complete your bank transfer</div>
    <p className="text-sm text-amber-700 mb-3">Your registration is <strong>Pending Payment</strong>. Transfer {usd(o.total)} and include reference <strong>{o.reference}</strong>. We'll confirm once funds are received.</p>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      <div><span className="text-slate-500">Bank:</span> <strong>{bank.bank}</strong></div>
      <div><span className="text-slate-500">Account holder:</span> <strong>{bank.account_holder}</strong></div>
      <div><span className="text-slate-500">Account #:</span> <strong className="font-mono">{bank.account_number}</strong></div>
      <div><span className="text-slate-500">ACH routing:</span> <strong className="font-mono">{bank.routing_ach}</strong></div>
      <div><span className="text-slate-500">Wire routing:</span> <strong className="font-mono">{bank.routing_wire}</strong></div>
    </div>
  </div>
);

export const JerseyGift = ({ gift, size, setSize, onSave }) => (
  <div data-testid="jersey-gift-section" className="rounded-xl bg-amber-50 border-2 border-[#F3C242] p-4">
    <div className="flex items-center gap-2 font-bold text-[#03281B] mb-1"><Gift size={18} className="text-[#B8860B]" /> Cristiano Ronaldo Jersey Awarded!</div>
    <p className="text-sm text-slate-600 mb-3">Congratulations — {gift.participant_name} qualifies for the retro replica jersey. Please confirm the size below.</p>
    <div className="flex gap-2">
      <select data-testid="jersey-size-select" className="rounded-xl border-2 border-[#E2EBF0] px-3 py-2 text-sm flex-1" value={size} onChange={(e) => setSize(e.target.value)}>
        <option value="">Select jersey size</option>{JERSEY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button data-testid="save-jersey-size" onClick={onSave} disabled={!size} className="rounded-xl bg-[#008057] text-white px-4 py-2 font-bold text-sm disabled:opacity-50">Save</button>
    </div>
  </div>
);

const ParticipantLines = ({ participants }) => participants.map((p) => (
  <div key={personKey(p)} className="border-t border-slate-100 py-2">
    <div className="font-semibold text-[#03281B]">{p.first_name} {p.last_name}</div>
    {(p.items || []).map((it) => (
      <div key={itemKey(it)} className="flex justify-between text-sm text-slate-600"><span>{it.label}</span><span className="font-mono">{usd(it.amount)}</span></div>
    ))}
  </div>
));

const LockedSummary = ({ count, onEmail, busy }) => (
  <div className="space-y-3">
    <div className="text-sm text-slate-600" data-testid="locked-participant-count">{count} participant(s)</div>
    <EmailGate onSubmit={onEmail} busy={busy} />
  </div>
);

export const OrderSummaryBlock = ({ order: o, locked, onEmail, busy }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-2">Order Summary</div>
    <div className="text-sm text-slate-600 mb-2">{o.category?.name} &middot; {o.payment_method === "stripe" ? "Credit Card" : "Bank Transfer"}</div>
    {locked ? <LockedSummary count={o.participant_count} onEmail={onEmail} busy={busy} /> : <ParticipantLines participants={o.participants} />}
    <div className="flex justify-between font-display font-extrabold text-xl text-[#03281B] border-t-2 border-[#008057] pt-2 mt-2"><span>{o.registration_status === "confirmed" ? "Amount Paid" : "Total Due"}</span><span>{usd(o.total)}</span></div>
  </div>
);
